import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";

import { getAccessToken, setAccessToken, setUnauthenticatedHandler } from "../api/client";
import { auth as authApi } from "../api/resources";
import type { Membership, SignedInUser } from "./types";

interface AuthContextValue {
  user: SignedInUser | undefined;
  memberships: Membership[];
  loading: boolean;
  signedIn: boolean;
  /**
   * Whether this deployment enforces sign-in. `AUTH_REQUIRED=false` exists for the test suite and
   * local exploration; showing a sign-in wall in front of an API that ignores tokens would be
   * theatre, so the interface asks rather than assumes.
   */
  authRequired: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => void;
  /** Adopt a session handed back by the invitation-accept endpoint. */
  adopt: (token: string, user: SignedInUser, memberships: Membership[]) => void;
  refresh: () => void;
  /** What the signed-in user may do in a given organization. */
  can: (organizationId: string | undefined, permission: string) => boolean;
  roleIn: (organizationId: string | undefined) => string | undefined;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<SignedInUser | undefined>();
  const [memberships, setMemberships] = useState<Membership[]>([]);
  const [loading, setLoading] = useState(true);
  const [authRequired, setAuthRequired] = useState(true);
  const [nonce, setNonce] = useState(0);

  const clear = useCallback(() => {
    setAccessToken(null);
    setUser(undefined);
    setMemberships([]);
    setLoading(false);
  }, []);

  // A token that expires mid-session signs the user out here rather than in every caller.
  useEffect(() => {
    setUnauthenticatedHandler(clear);
    return () => setUnauthenticatedHandler(undefined);
  }, [clear]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);

    async function load() {
      try {
        const config = await authApi.config();
        if (!cancelled) setAuthRequired(config.auth_required);
      } catch {
        /* an unreachable API is reported by the pages themselves; assume enforcement */
      }
      if (!getAccessToken()) return;
      try {
        const body = await authApi.me();
        if (cancelled) return;
        setUser(body.user);
        setMemberships(body.memberships);
      } catch {
        if (!cancelled) clear();
      }
    }

    void load().finally(() => {
      if (!cancelled) setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [nonce, clear]);

  const value = useMemo<AuthContextValue>(() => {
    const byOrganization = new Map(memberships.map((row) => [row.organization_id, row]));

    return {
      user,
      memberships,
      loading,
      signedIn: Boolean(user),
      authRequired,
      signIn: async (email, password) => {
        const body = await authApi.login(email, password);
        setAccessToken(body.access_token);
        setUser(body.user);
        setMemberships(body.memberships);
      },
      signOut: clear,
      adopt: (token, nextUser, nextMemberships) => {
        setAccessToken(token);
        setUser(nextUser);
        setMemberships(nextMemberships);
        setLoading(false);
      },
      refresh: () => setNonce((n) => n + 1),
      can: (organizationId, permission) => {
        // With enforcement off there is nobody to check, and hiding every control would make the
        // interface unusable in exactly the mode meant for exploring it.
        if (!authRequired && !user) return true;
        if (!user) return false;
        if (user.is_superuser) return true;
        if (!organizationId) return false;
        return byOrganization.get(organizationId)?.permissions.includes(permission) ?? false;
      },
      roleIn: (organizationId) => {
        if (!organizationId) return undefined;
        if (user?.is_superuser) return "owner";
        return byOrganization.get(organizationId)?.role_code;
      },
    };
  }, [user, memberships, loading, authRequired, clear]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used inside AuthProvider");
  return context;
}
